/**
 * Shared handshake payload for Socket.IO clients.
 *
 * Socket.IO sends `auth` once per handshake, so replacing it on a live socket takes effect at
 * the next connect rather than immediately. That is what makes this the right way to publish a
 * rotated token or a changed active role: the current connection is left alone and every
 * reconnect from then on carries the new payload.
 *
 * No imports, so this is unit testable on its own.
 */

/** The part of a Socket.IO client this module touches. */
export interface AuthenticatedSocket {
  auth: unknown;
}

/**
 * Give every socket the same handshake payload
 *
 * @param sockets The clients to update, typically one per namespace
 * @param payload The payload each of them should send on its next handshake
 * @returns How many sockets were updated
 */
export const applyAuthPayload = (
  sockets: Iterable<AuthenticatedSocket>,
  payload: Record<string, unknown>,
): number => {
  let updated = 0;
  for (const socket of sockets) {
    socket.auth = payload;
    updated += 1;
  }
  return updated;
};
